import { getContrast } from 'polished';

/**
 * Foreground color for content sitting ON the tenant brand accent
 * (`--brand-navy`, which Theme.store paints from `company.primary_color`).
 *
 * The shadcn bridge pairs `bg-primary` with `text-primary-foreground`. The
 * background half is tenant-driven, so a static white foreground only works
 * while every tenant picks a dark brand color — a light one (or a
 * near-white one) gives white-on-white and the text disappears. Deriving the
 * foreground from the brand color's own luminance keeps the pair legible for
 * ANY tenant color.
 *
 * The two candidates are static hexes on purpose. They must NOT flip with the
 * app's light/dark surface tokens (`--text-inverse` / `--text-primary` both
 * swap under `.dark-mode`): what makes this text readable is the brand color
 * underneath it, not the surrounding surface. Values mirror the light-mode
 * `--text-inverse` and `--text-primary` in `src/ui-new/tokens.css`.
 */
export const BRAND_FOREGROUND_ON_DARK = '#FFFFFF';
export const BRAND_FOREGROUND_ON_LIGHT = '#1D293D';

/**
 * Pick the legible foreground for a resolved brand color: whichever of the two
 * candidates has the higher WCAG contrast ratio against it.
 *
 * This deliberately compares the two candidates rather than reusing
 * `isDarkBackground` (a "does white clear AA on this?" test). The two agree for
 * every realistic brand color; they diverge only in the narrow mid-tone band
 * (around #808080) where NEITHER candidate clears AA — there the threshold test
 * would hand back the worse of the two, while max-contrast still returns the
 * most readable option available.
 *
 * An unset or unparseable color falls back to the on-dark (white) value,
 * matching the static `--brand-foreground` default in tokens.css that pairs
 * with the default navy accent.
 */
export const resolveBrandForeground = (
  brandColor: string | undefined | null
): string => {
  if (!brandColor) return BRAND_FOREGROUND_ON_DARK;
  try {
    return getContrast(brandColor, BRAND_FOREGROUND_ON_LIGHT) >
      getContrast(brandColor, BRAND_FOREGROUND_ON_DARK)
      ? BRAND_FOREGROUND_ON_LIGHT
      : BRAND_FOREGROUND_ON_DARK;
  } catch {
    return BRAND_FOREGROUND_ON_DARK;
  }
};
